import React from "react"

// Models
import {AvailableRecordType} from "../../models"

// HOCs
import {WithDoctorAvailableRecordsType} from "../../hoc/withDoctorAvailableRecords"

// Utils
import {RecordsPeriod} from "../../utils/dateRange"

/**
 * Bridges the server-paginated `doctor-stats` slice (whose reducer REPLACES the
 * record array on every fetch) to the Modern dashboard's infinite list (which
 * needs the union of every page loaded so far).
 *
 * Everything below is component-local with ONE exception: the shared slice
 * gained an `availableRecordsError` flag (plus its selector and HOC mapping),
 * because a failed fetch was otherwise indistinguishable from one still in
 * flight and left this hook pending forever. Classic ignores the flag.
 *
 * Three production constraints are encoded here and must not be "simplified":
 *
 * 1. `setAvailableRecordSize` is NOT in `fetchDoctorAvailableRecordsEpic`'s
 *    `isActionOf([...])` filter — dispatching it alone never refetches. The size
 *    setter is therefore ALWAYS followed by `setAvailableRecordsPage(0)`, which
 *    is what actually triggers the request (the reducer runs before the epic
 *    reads `state$`, so the new size and page are both visible to it).
 * 2. On unmount we restore size "5" / page 0. Without that, flipping back to
 *    Classic shows AvailableRecordTable parked on page N with a rowsPerPage
 *    value that is not in its `rowsPerPageOptions`.
 * 3. `availableRecordsSize` is typed `number` but becomes a `string` after the
 *    first setter call. It is deliberately never read here.
 * 4. `availableRecordsFetching` is NOT a usable in-flight flag for pagination:
 *    it is set by `fetchAvailableRecordsAsync.request`, which the epic never
 *    dispatches for a `setAvailableRecordsPage` fetch (only the websocket
 *    refetch goes through `request`). This hook tracks its own pending flag —
 *    see `pendingRef`.
 *
 *
 * Known, accepted limitation: sorting and the search box still operate on
 * LOADED rows only — the endpoint exposes no sort or query parameter. The
 * footer reads "{visible} / {total}" so the user can see that more rows exist
 * beyond what is currently sortable.
 */

/** Rows requested per page while the Modern dashboard is mounted. */
export const MODERN_PAGE_SIZE = "10";

/** AvailableRecordTable's default — restored on unmount. */
const CLASSIC_PAGE_SIZE = "5";

export type AccumulatedRecords = {
    rows: Array<AvailableRecordType>
    total: number
    loading: boolean
    done: boolean
    error: boolean
    loadMore(): void
    retry(): void
    pendingCount: number
    applyPending(): void
}

type MergeResult = {
    next: Array<AvailableRecordType>
    added: number
    additions: Array<AvailableRecordType>
}

/**
 * Merge an incoming page into the accumulated list.
 *
 * The websocket `updateDoctorListEvent` refetches the CURRENT page only, so an
 * incoming record may already be present — it is replaced in place rather than
 * appended, which keeps the list stable while a status changes underneath it.
 * Returns the previous array unchanged when nothing moved, so the state update
 * cannot loop.
 *
 * Records are tagged with the page they arrived on, and a re-fetch of page P
 * DROPS any row previously seen on P that the server no longer returns. Without
 * that removal pass, a record another doctor has just taken stayed in this
 * doctor's list with a live "Review" button until they remounted the dashboard
 * — which is precisely what `updateDoctorListEvent` exists to prevent. (Classic
 * replaces the array wholesale and so never had the problem.)
 *
 * KNOWN LIMITATION: the websocket refetch only ever re-reads
 * `availableRecordsCurrentPage`, so a row that disappears from page 0 while the
 * doctor has already scrolled to page 2 is not detected. That is a property of
 * the endpoint's pagination, not of this merge — fixing it would mean
 * re-fetching every loaded page on each event. The stale row is still guarded
 * server-side: opening an already-assigned record fails the assign call rather
 * than handing the doctor someone else's work.
 *
 * @param prev
 * @param incoming
 * @param page the page `incoming` is the content of
 * @param pageById mutable id -> page tag map, owned by the caller
 * @param holdAdditions withhold new records from `next` and return them instead
 */
function mergeRecords(
  prev: Array<AvailableRecordType>,
  incoming: Array<AvailableRecordType>,
  page: number,
  pageById: {[id: string]: number},
  holdAdditions?: boolean,
): MergeResult {
    if (!incoming) {
        return {next: prev, added: 0, additions: []}
    }

    const incomingIds: {[id: string]: true} = {};

    for (let i = 0; i < incoming.length; i++) {
        if (incoming[i]) {
            incomingIds[incoming[i].teleRecordId] = true
        }
    }

    // Drop rows this page used to contain and no longer does.
    let next: Array<AvailableRecordType> = [];
    let removed = 0;

    for (let i = 0; i < prev.length; i++) {
        const record = prev[i];
        const id = record.teleRecordId;

        if (pageById[id] === page && !incomingIds[id]) {
            delete pageById[id];
            removed++;

            continue
        }

        next.push(record)
    }

    const indexById: {[id: string]: number} = {};

    for (let i = 0; i < next.length; i++) {
        indexById[next[i].teleRecordId] = i
    }

    let replaced = 0;
    let added = 0;
    const additions: Array<AvailableRecordType> = [];

    for (let i = 0; i < incoming.length; i++) {
        const record = incoming[i];

        if (!record) {
            continue
        }

        const id = record.teleRecordId;
        const at = indexById[id];

        if (typeof at === "number") {
            pageById[id] = page;

            if (next[at] !== record) {
                next[at] = record;
                replaced++
            }

            continue
        }

        if (holdAdditions) {
            additions.push(record);

            continue
        }

        pageById[id] = page;
        indexById[id] = next.length;
        next.push(record);
        added++
    }

    if (removed === 0 && replaced === 0 && added === 0) {
        return {next: prev, added: 0, additions}
    }

    return {next, added, additions}
}

/**
 * @param props the `withDoctorAvailableRecords` slice, passed straight through
 * @param enabled `false` for company masters, who never see the records card —
 *        this mirrors Classic, where AvailableRecordTable is simply not mounted
 *        and therefore never fetches
 * @param period the active tab/stat-card filter, sent to the server as a date
 *        range; changing it restarts the accumulation from page 0
 */
export default function useAccumulatedRecords(
  props: WithDoctorAvailableRecordsType,
  enabled: boolean,
  period: RecordsPeriod,
): AccumulatedRecords {
    const {
        availableRecords,
        availableRecordsTotal,
        availableRecordsCurrentPage,
        availableRecordsFetching,
        availableRecordsError,
        setAvailableRecordsPage,
        setAvailableRecordsSize,
        setAvailableRecordsPeriod,
    } = props;

    const [rows, setRows] = React.useState<Array<AvailableRecordType>>([]);
    const [pending, setPending] = React.useState(false);
    const [exhausted, setExhausted] = React.useState(false);
    const [held, setHeld] = React.useState<Array<AvailableRecordType>>([]);
    const heldRef = React.useRef<Array<AvailableRecordType>>([]);
    const heldPageByIdRef = React.useRef<{[id: string]: number}>({});

    // Mirrors `rows` so the effects below can read the CURRENT list without
    // re-subscribing to it (a `rows` dependency would re-run them on every
    // merge and re-open the request loop this hook exists to prevent).
    const rowsRef = React.useRef<Array<AvailableRecordType>>([]);

    // Which page each accumulated record last arrived on — drives the removal
    // pass in mergeRecords.
    const pageByIdRef = React.useRef<{[id: string]: number}>({});

    // Highest page already asked for.
    const requestedPageRef = React.useRef(0);

    /**
     * A page request we issued is still outstanding.
     *
     * This CANNOT be `availableRecordsFetching`: the epic reacts to
     * `setAvailableRecordsPage` by calling the service directly and only maps
     * the SUCCESS action, so `fetchAvailableRecordsAsync.request` — the only
     * action that sets `availableRecordsFetching` — is never dispatched for a
     * page change. `availableRecordsFetching` therefore stays false for the
     * whole of an infinite-scroll fetch, and using it as the in-flight guard
     * let the sentinel effect dispatch page N+1, N+2, N+3 … in a single burst
     * (each dispatch changes `availableRecordsCurrentPage`, which re-created
     * `loadMore`, which re-ran the effect).
     *
     * A request is considered settled when `availableRecords` next changes
     * identity, OR when `availableRecordsError` rises. The error flag is what
     * makes the failure path observable at all: a failed fetch leaves
     * `availableRecords` at the same reference, so without it this ref stayed
     * true forever and the card was stuck reading "Loading…" — no empty state,
     * no error, no retry — until a full page reload.
     */
    const pendingRef = React.useRef(false);

    // The slice is shared with Classic's AvailableRecordTable and may already
    // hold page N at size 5 when Modern mounts. That content belongs to the
    // other skin's pagination and must not be merged into the Modern list.
    const ignoreStoreRef = React.useRef(true);

    const periodRef = React.useRef(period);

    /**
     * @param ignoreStore swallow the next store update, for when the slice still
     *        holds rows that are not ours to merge
     */
    const resetAccumulation = React.useCallback((ignoreStore: boolean) => {
        requestedPageRef.current = 0;
        rowsRef.current = [];
        pageByIdRef.current = {};
        heldRef.current = [];
        heldPageByIdRef.current = {};
        ignoreStoreRef.current = ignoreStore;
        pendingRef.current = true;
        setRows([]);
        setHeld([]);
        setExhausted(false);
        setPending(true)
    }, []);

    React.useEffect(() => {
        if (!enabled) {
            return () => undefined
        }

        // The slice may still hold Classic's page N at size 5 — see below.
        resetAccumulation(true);

        // ORDER IS LOAD-BEARING — see the file header.
        setAvailableRecordsSize(MODERN_PAGE_SIZE);
        setAvailableRecordsPeriod(periodRef.current);
        setAvailableRecordsPage(0);

        return () => {
            pendingRef.current = false;
            setAvailableRecordsSize(CLASSIC_PAGE_SIZE);
            setAvailableRecordsPeriod("all");
            setAvailableRecordsPage(0)
        }
    }, [enabled, resetAccumulation, setAvailableRecordsSize, setAvailableRecordsPeriod, setAvailableRecordsPage]);
    React.useEffect(() => {
        if (!enabled || periodRef.current === period) {
            return
        }

        periodRef.current = period;
        resetAccumulation(false);

        // ORDER IS LOAD-BEARING — see the file header.
        setAvailableRecordsPeriod(period);
        setAvailableRecordsPage(0)
    }, [enabled, period, resetAccumulation, setAvailableRecordsPeriod, setAvailableRecordsPage]);

    React.useEffect(() => {
        if (ignoreStoreRef.current) {
            ignoreStoreRef.current = false;

            return
        }

        const push = false === pendingRef.current;
        const prev = rowsRef.current;
        const {next, added, additions} = mergeRecords(
          prev,
          availableRecords,
          availableRecordsCurrentPage,
          pageByIdRef.current,
          push,
        );

        if (next !== prev) {
            rowsRef.current = next;
            setRows(next)
        }

        if (push) {
            if (!availableRecords) {
                return
            }

            const incomingIds: {[id: string]: true} = {};

            for (let i = 0; i < availableRecords.length; i++) {
                if (availableRecords[i]) {
                    incomingIds[availableRecords[i].teleRecordId] = true
                }
            }

            const kept: Array<AvailableRecordType> = [];
            const keptIndex: {[id: string]: number} = {};

            for (let i = 0; i < heldRef.current.length; i++) {
                const record = heldRef.current[i];
                const id = record.teleRecordId;

                if (heldPageByIdRef.current[id] === availableRecordsCurrentPage && !incomingIds[id]) {
                    delete heldPageByIdRef.current[id];

                    continue
                }

                keptIndex[id] = kept.length;
                kept.push(record)
            }

            let changed = kept.length !== heldRef.current.length;

            for (let i = 0; i < additions.length; i++) {
                const record = additions[i];
                const id = record.teleRecordId;
                const at = keptIndex[id];

                heldPageByIdRef.current[id] = availableRecordsCurrentPage;

                if (typeof at === "number") {
                    if (kept[at] !== record) {
                        kept[at] = record;
                        changed = true
                    }

                    continue
                }

                keptIndex[id] = kept.length;
                kept.push(record);
                changed = true
            }

            if (changed) {
                heldRef.current = kept;
                setHeld(kept)
            }

            // A websocket-driven refetch of the current page, not an answer to
            // one of our own page requests — it must not affect `exhausted`.
            return
        }

        pendingRef.current = false;
        setPending(false);

        // A load-more page that brought nothing new means the endpoint has no
        // more to give, whatever `totalElements` claims (short page, repeated
        // ids, or a page past the end). Without this the sentinel would keep
        // asking for the next page for as long as `rows.length < total`.
        //
        // Counts ADDED rows rather than comparing lengths: the removal pass can
        // now shrink the list, which would otherwise make a page that did bring
        // new records look empty.
        if (requestedPageRef.current > 0 && added === 0) {
            setExhausted(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableRecords]);

    // Settle the in-flight guard when the request failed. Deliberately keyed on
    // the error flag alone: re-running this on `availableRecords` would clear
    // `pendingRef` for the *next* request too, since the flag stays true until
    // something dispatches again.
    React.useEffect(() => {
        if (!availableRecordsError || !pendingRef.current) {
            return
        }

        pendingRef.current = false;
        setPending(false)
    }, [availableRecordsError]);

    const total = availableRecordsTotal;
    const done = exhausted || rows.length >= total;
    const applyPending = React.useCallback(() => {
        if (heldRef.current.length === 0) {
            return
        }

        const merged = rowsRef.current.concat(heldRef.current);

        for (let i = 0; i < heldRef.current.length; i++) {
            const id = heldRef.current[i].teleRecordId;
            const page = heldPageByIdRef.current[id];

            if (typeof page === "number") {
                pageByIdRef.current[id] = page;
                delete heldPageByIdRef.current[id]
            }
        }

        rowsRef.current = merged;
        heldRef.current = [];
        setRows(merged);
        setHeld([])
    }, []);

    const retry = React.useCallback(() => {
        if (!enabled || pendingRef.current) {
            return
        }

        pendingRef.current = true;
        setPending(true);
        // The epic filters on the action, not on a page-value change, so
        // re-dispatching the same page number does re-issue the request.
        setAvailableRecordsPage(requestedPageRef.current)
    }, [enabled, setAvailableRecordsPage]);

    const loadMore = React.useCallback(() => {
        if (!enabled || pendingRef.current || exhausted || availableRecordsError) {
            return
        }

        if (rowsRef.current.length >= availableRecordsTotal) {
            return
        }

        const next = availableRecordsCurrentPage + 1;

        if (next <= requestedPageRef.current) {
            return
        }

        requestedPageRef.current = next;
        pendingRef.current = true;
        setPending(true);
        setAvailableRecordsPage(next)
    }, [
        enabled,
        exhausted,
        availableRecordsError,
        availableRecordsCurrentPage,
        availableRecordsTotal,
        setAvailableRecordsPage,
    ]);

    return {
        rows,
        total,
        loading: pending || availableRecordsFetching,
        done,
        error: availableRecordsError,
        loadMore,
        retry,
        pendingCount: held.length,
        applyPending,
    }
}
